import React, {memo, useState} from "react";
import {
    AppBar,
    Box,
    Drawer,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Divider,
    Modal, IconButton, Popover, Typography,
} from "@mui/material";
import {Arrow, Check, Chevron, EditIcon, Logo, LogoKids, SmallLogo, SmallLogoKids} from "../../assets/files";
import {useNavData, useNavKidsData} from "./IAside";
import styles from "./Aside.module.css";
import {useLocation, useNavigate} from "react-router-dom";
import {routes} from "../../routes/routes";
import {useDispatch} from "react-redux";
import {AppDispatch} from "../../store";
import helper from "../../constants/helper";
import Avatar from "../../assets/images/Avatar.png";
import {setProfileID, setRole, setUser} from "../../store/reducer";
import Keys from "../../constants/helper";
import clsx from "clsx";
import {useTranslation} from "react-i18next";
import {useGetProfilesByIdQuery, useGetProfilesQuery} from "../../api/modules";

interface IProps {
    withoutMenu?: boolean,
    hideBackButton?: boolean,
    route?: string,
    handleBack?: () => void,
}

export interface IProfile {
    avatar: {
        file: string
        id: number
    };
    id: number;
    is_kids: boolean;
    language: string | null;
    name: string;
}

const Aside: React.FC<IProps> = ({withoutMenu = false, hideBackButton = false, route, handleBack}) => {
    const {t} = useTranslation("translations");
    const role = localStorage.getItem(helper.EPIC_USER_ROLE);
    const profileID = localStorage.getItem(helper.EPIC_PROFILE_ID) as string;
    const dispatch: AppDispatch = useDispatch()
    const navigate = useNavigate()
    const location = useLocation()
    const [open, setOpen] = useState<boolean>(false);
    const handleDrawerToggle = () => !withoutMenu && setOpen(prevOpen => !prevOpen);
    const {data: list} = useGetProfilesQuery()
    const {data: profile} = useGetProfilesByIdQuery(profileID, {
        skip: !profileID,
    });


    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const openPopup = Boolean(anchorEl);
    const navData = useNavData()
    const navDataKIDS = useNavKidsData()

    const dataToMap = role === "KIDS" ? navDataKIDS : navData;

    const handleOpenPopover = (event: React.MouseEvent<HTMLElement>) => {
        if (open) {
            if (anchorEl === event.currentTarget) {
                setAnchorEl(null);
            } else {
                setAnchorEl(event.currentTarget);
            }
        }
    };
    const handleNavigateToEdit = () => {
        navigate(routes.profile)
        localStorage.removeItem(Keys.EPIC_PROFILE_ID);
        localStorage.removeItem(Keys.EPIC_USER_ROLE);
    }

    const handleChangeLink = (path?: string) => {
        if (path) {
            setOpen(false);
            navigate(path);

        }
    };

    const handleBackDefault = () => {
        if (!route) {
            window.history.back(); // Go back to the previous page
        } else {
            navigate(route ?? routes.home, {replace: true}); // Replace to avoid back-loop between detail screens
        }
    }

    const handleChangeProfile = (e: IProfile) => {
        if (e) {
            console.log("e", e)
            dispatch(setProfileID(e?.id.toString()))
            dispatch(setUser(e))
            dispatch(setRole(e.is_kids ? "KIDS" : "USER"))
            window.location.href = routes.home;
        }

    }

    const handleNavigate = () => {
        if (!profileID) return
        navigate(routes.home)
    }


    const renderContent = () => (
        <Box className={styles.box}>
            <AppBar className={clsx(styles.logo, open && styles.logoOpened)}>
                {open ? <IconButton onClick={handleNavigate}>{role == "KIDS" ? <LogoKids size={145} height={48}/> :
                        <Logo size={145} height={48}/>}
                    </IconButton> :
                    <IconButton onClick={handleNavigate}>
                        {role === "KIDS" ? <SmallLogoKids/> : <SmallLogo/>}
                    </IconButton>}
            </AppBar>
            {withoutMenu ? (
                hideBackButton ? null :
                <div
                    className={styles.arrow}
                    onClick={handleBack ? handleBack : handleBackDefault}>
                    <Arrow/>
                </div>
            ) :
                <Drawer
                    variant={"permanent"}
                    open={open}
                    onClose={handleDrawerToggle}
                >
                    <List>
                        <ListItem disablePadding>
                            <ListItemButton sx={{
                                minHeight: 48,
                                justifyContent: open ? "initial" : "center",
                                px: 2.5,
                                borderRadius: 1,
                                opacity: 1,
                            }}>
                                <ListItemIcon
                                    onClick={handleDrawerToggle}
                                    sx={{
                                        minWidth: 0,
                                        mr: open ? 3 : "0",
                                        justifyContent: "center",
                                    }}
                                >
                                    <img src={profile?.avatar?.file || Avatar} alt={"file"} loading={"lazy"}
                                         className={styles.profile_image}/>

                                </ListItemIcon>
                                <ListItemText
                                    onClick={handleOpenPopover}
                                    className={styles.text}
                                    primary={profile?.name}
                                    sx={{
                                        marginLeft: open ? "-8px" : "0",
                                        opacity: open ? 1 : 0,
                                        maxWidth: open ? "200px" : "0px", // Adjust maxWidth as needed
                                        transition: "opacity 0.3s ease, max-width 0.3s ease",
                                        width: "80px",
                                        marginRight: open ? ".5rem" : 0
                                    }}
                                />
                                <div onClick={handleOpenPopover}>
                                    {open && <Chevron rotate={openPopup ? 180 : 0}/>}
                                </div>
                            </ListItemButton>
                        </ListItem>
                        {dataToMap.map((item, index) => (
                            <React.Fragment key={item.key}>
                                {index === navData.length - 9 && <Divider style={{opacity: .16}}/>}
                                <ListItem disablePadding>
                                    <ListItemButton
                                        sx={{
                                            minHeight: 48,
                                            justifyContent: open ? "initial" : "center",
                                            px: 2.5,
                                            borderRadius: 1,
                                        }}
                                        selected={location?.pathname === item.path}
                                        onClick={() => handleChangeLink(item.path)}
                                    >
                                        <ListItemIcon
                                            sx={{
                                                minWidth: 0,
                                                mr: open ? 3 : "0",
                                                justifyContent: "center",
                                            }}
                                        >
                                            <item.icon/>
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={item.label}
                                            sx={{
                                                opacity: open ? 1 : 0,
                                                maxWidth: open ? "200px" : "0px", // Adjust maxWidth as needed
                                                transition: "opacity 0.3s ease, max-width 0.3s ease",
                                                overflow: "hidden", // Prevent text overflow when hidden
                                                whiteSpace: "nowrap" // Prevent text wrapping
                                            }}
                                        />
                                    </ListItemButton>
                                </ListItem>
                                {index === navData.length - 2 && <Divider style={{opacity: .16,}}/>}
                            </React.Fragment>
                        ))}
                    </List>
                </Drawer>}
            <Popover
                onClose={() => setAnchorEl(null)}
                open={openPopup}
                anchorEl={anchorEl}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "left",
                }}>
                <div className={styles.popoverAudio}>
                    {list?.map((e) => (
                        <div key={e.id} className={e.id === profile?.id ? styles.active : ""}
                             onClick={() => handleChangeProfile(e)}>
                            <img src={e.avatar?.file || Avatar} alt={"file"}/>
                            <Typography variant={"h5"}>{e.name}</Typography>
                            {e.id === profile?.id && <Check/>}
                        </div>
                    ))}
                    <div className={styles.edit} onClick={handleNavigateToEdit}>
                        <div><EditIcon/></div>
                        <Typography variant={"h5"}>{t("Authorization.editProfiles")}</Typography>
                    </div>
                </div>
            </Popover>
        </Box>
    );
    return (<>{open ? (
        <Modal open={open} onClose={handleDrawerToggle}>{renderContent()}</Modal>) : (renderContent())}</>);
};

export default memo(Aside);
